import React from 'react';
import { Box, Checkbox, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { ScheduleTask } from '../../services/schedulesService';
import { computeSummaryRollups, type TaskRollup } from './schedulingEngine';

/**
 * Edytowalna tabela zadań harmonogramu.
 *
 * Obsługa:
 *   - Inline edycja wszystkich kolumn (oprócz ID) — zmiana idzie w `onChange`.
 *   - `outline_level` — wcięcie w kolumnie Opis (8px na poziom) + pogrubienie summary.
 *   - Tab / Shift+Tab w komórce „Opis" → indent / outdent (onIndent/onOutdent).
 *   - Checkbox zaznaczenia w pierwszej kolumnie (do łączenia zadań poprzednikami).
 *   - Summary (zadanie z dziećmi): kolumny Dni/Start/End/Wartość/Zaaw. są READONLY
 *     i wyświetlają wartości wyliczone (computeSummaryRollups).
 */

export interface ScheduleTaskTableProps {
  tasks: ScheduleTask[];
  onChange: (next: ScheduleTask[]) => void;
  onDelete: (taskId: number) => void;
  onAddBelow?: (afterId: number) => void;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onIndent: (taskId: number) => void;
  onOutdent: (taskId: number) => void;
  readOnly?: boolean;
}

type ColumnKey =
  | 'select'
  | 'ordinal'
  | 'ssp'
  | 'opis'
  | 'days'
  | 'start_date'
  | 'end_date'
  | 'predecessor'
  | 'value'
  | 'notes'
  | 'progress_pct'
  | 'progress_date'
  | 'akcje';

type ColumnDef = {
  key: ColumnKey;
  label: string;
  widthPx: number;
  align?: 'left' | 'right' | 'center';
  editable?: boolean;
  type?: 'number' | 'date' | 'percent' | 'currency' | 'text';
};

const COLUMNS: ColumnDef[] = [
  { key: 'select', label: '', widthPx: 32, align: 'center', editable: false },
  { key: 'ordinal', label: 'ID', widthPx: 48, align: 'right', editable: false, type: 'number' },
  { key: 'ssp', label: 'SPP', widthPx: 80, editable: true },
  { key: 'opis', label: 'Opis', widthPx: 380, editable: true },
  { key: 'days', label: 'Dni', widthPx: 54, align: 'right', editable: true, type: 'number' },
  { key: 'start_date', label: 'Rozpoczęcie', widthPx: 110, editable: true, type: 'date' },
  { key: 'end_date', label: 'Zakończenie', widthPx: 110, editable: true, type: 'date' },
  { key: 'predecessor', label: 'Poprzednik', widthPx: 90, editable: true },
  { key: 'value', label: 'Wartość', widthPx: 110, align: 'right', editable: true, type: 'currency' },
  { key: 'notes', label: 'Uwagi', widthPx: 180, editable: true },
  { key: 'progress_pct', label: 'Zaaw. [%]', widthPx: 70, align: 'right', editable: true, type: 'percent' },
  { key: 'progress_date', label: 'Data stanu', widthPx: 110, editable: true, type: 'date' },
  { key: 'akcje', label: 'Akcje', widthPx: 48, align: 'center', editable: false },
];

const HEADER_HEIGHT = 36;
const ROW_HEIGHT = 32;
const INDENT_PX = 16;

function addDays(yyyyMmDd: string, days: number): string {
  const d = new Date(`${yyyyMmDd}T12:00:00`);
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function formatMoneyPl(n: number): string {
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseMoney(s: string): number {
  const n = Number(String(s).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

const ScheduleTaskTable: React.FC<ScheduleTaskTableProps> = ({
  tasks,
  onChange,
  onDelete,
  onAddBelow,
  selectedIds,
  onSelectionChange,
  onIndent,
  onOutdent,
  readOnly = false,
}) => {
  const [menuState, setMenuState] = React.useState<{ anchor: HTMLElement; taskId: number } | null>(null);

  const rollups = React.useMemo<Map<number, TaskRollup>>(() => computeSummaryRollups(tasks), [tasks]);

  const updateRow = (id: number, patch: Partial<ScheduleTask>) => {
    const next = tasks.map((t) => {
      if (t.id !== id) return t;
      const merged = { ...t, ...patch };
      if ((patch.days != null || patch.start_date != null) && patch.end_date == null) {
        merged.end_date = addDays(merged.start_date, Math.max(1, Number(merged.days) || 1) - 1);
      }
      if (patch.end_date != null && patch.days == null) {
        const d1 = new Date(`${merged.start_date}T12:00:00`);
        const d2 = new Date(`${merged.end_date}T12:00:00`);
        const diff = Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        merged.days = diff > 0 ? diff : 1;
      }
      return merged;
    });
    onChange(next);
  };

  const toggleSelected = (id: number, checked: boolean) => {
    if (checked) {
      if (!selectedIds.includes(id)) onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    }
  };

  const renderCell = (task: ScheduleTask, col: ColumnDef): React.ReactNode => {
    const rollup = rollups.get(task.id);
    const isSummary = !!rollup?.isSummary;

    if (col.key === 'select') {
      return (
        <Checkbox
          size="small"
          checked={selectedIds.includes(task.id)}
          onChange={(e) => toggleSelected(task.id, e.target.checked)}
          sx={{ p: 0.25 }}
        />
      );
    }

    if (col.key === 'akcje') {
      return (
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={(e) => setMenuState({ anchor: e.currentTarget, taskId: task.id })}
          aria-label={`Akcje zadania ${task.ordinal}`}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      );
    }

    if (col.key === 'ordinal') return <span>{task.ordinal}</span>;

    if (col.key === 'opis') {
      const indentPx = (task.outline_level || 0) * INDENT_PX;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            paddingLeft: `${indentPx}px`,
          }}
        >
          {isSummary && (
            <Box
              aria-hidden
              sx={{
                width: 0,
                height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '6px solid #020242',
                mr: 0.5,
                flexShrink: 0,
              }}
            />
          )}
          <input
            type="text"
            value={task.opis ?? ''}
            onChange={(e) => updateRow(task.id, { opis: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                /* Tab → indent, Shift+Tab → outdent — nie przełączaj fokusu. */
                e.preventDefault();
                if (e.shiftKey) onOutdent(task.id);
                else onIndent(task.id);
              }
            }}
            style={{
              ...cellInputStyle(col),
              fontWeight: isSummary ? 600 : 400,
              color: isSummary ? '#020242' : 'inherit',
            }}
          />
        </Box>
      );
    }

    /* Summary → wartości wyliczone, readonly. */
    if (isSummary && rollup) {
      if (col.key === 'days')
        return <span style={{ fontWeight: 600 }}>{rollup.days}</span>;
      if (col.key === 'start_date')
        return <span style={{ fontWeight: 600 }}>{rollup.start_date}</span>;
      if (col.key === 'end_date')
        return <span style={{ fontWeight: 600 }}>{rollup.end_date}</span>;
      if (col.key === 'value')
        return <span style={{ fontWeight: 600 }}>{formatMoneyPl(rollup.value)}</span>;
      if (col.key === 'progress_pct')
        return <span style={{ fontWeight: 600 }}>{rollup.progress_pct}</span>;
    }

    const value = (task as unknown as Record<string, unknown>)[col.key];

    if (col.editable && !readOnly) {
      if (col.type === 'date') {
        return (
          <input
            type="date"
            value={(value as string) || ''}
            onChange={(e) =>
              updateRow(task.id, { [col.key]: e.target.value || task.start_date } as Partial<ScheduleTask>)
            }
            style={cellInputStyle(col)}
          />
        );
      }
      if (col.type === 'number') {
        return (
          <input
            type="number"
            min={1}
            value={String(value ?? '')}
            onChange={(e) => {
              const n = parseInt(e.target.value || '1', 10);
              updateRow(task.id, { [col.key]: Number.isFinite(n) && n > 0 ? n : 1 } as Partial<ScheduleTask>);
            }}
            style={cellInputStyle(col)}
          />
        );
      }
      if (col.type === 'percent') {
        return (
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={String(value ?? 0)}
            onChange={(e) => {
              const n = parseMoney(e.target.value);
              updateRow(task.id, { progress_pct: Math.max(0, Math.min(100, n)) });
            }}
            style={cellInputStyle(col)}
          />
        );
      }
      if (col.type === 'currency') {
        const display = formatMoneyPl(Number(value) || 0);
        return (
          <input
            type="text"
            defaultValue={display}
            key={`${task.id}:${display}`}
            onBlur={(e) => updateRow(task.id, { value: parseMoney(e.target.value) })}
            style={cellInputStyle(col)}
          />
        );
      }
      return (
        <input
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => updateRow(task.id, { [col.key]: e.target.value } as Partial<ScheduleTask>)}
          style={cellInputStyle(col)}
        />
      );
    }
    if (col.type === 'currency') return <span>{formatMoneyPl(Number(value) || 0)}</span>;
    if (col.type === 'percent') return <span>{Number(value) || 0}</span>;
    return <span>{String(value ?? '')}</span>;
  };

  const totalWidth = COLUMNS.reduce((acc, c) => acc + c.widthPx, 0);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        bgcolor: '#FFFFFF',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        color: '#202124',
      }}
    >
      <Box sx={{ minWidth: totalWidth, position: 'relative' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: COLUMNS.map((c) => `${c.widthPx}px`).join(' '),
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: '#F1F3F4',
            borderBottom: '1px solid #dadce0',
            height: HEADER_HEIGHT,
          }}
        >
          {COLUMNS.map((c) => (
            <Box
              key={c.key}
              sx={{
                px: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
                fontWeight: 600,
                color: '#3c4043',
                borderRight: '1px solid #e8eaed',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {c.label}
            </Box>
          ))}
        </Box>

        {tasks.map((t, idx) => {
          const rollup = rollups.get(t.id);
          const isSummary = !!rollup?.isSummary;
          return (
            <Box
              key={t.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: COLUMNS.map((c) => `${c.widthPx}px`).join(' '),
                height: ROW_HEIGHT,
                borderBottom: '1px solid #f1f3f4',
                bgcolor: isSummary
                  ? '#EEF0F8'
                  : idx % 2 === 0
                    ? '#FFFFFF'
                    : '#FAFBFC',
                '&:hover': { bgcolor: isSummary ? '#E3E6F4' : '#EEF3FF' },
              }}
            >
              {COLUMNS.map((c) => (
                <Box
                  key={c.key as string}
                  sx={{
                    px: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
                    borderRight: '1px solid #f1f3f4',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {renderCell(t, c)}
                </Box>
              ))}
            </Box>
          );
        })}

        {tasks.length === 0 && (
          <Box sx={{ p: 3, color: '#5F6368', textAlign: 'center' }}>
            Brak zadań. Dodaj pierwsze z paska narzędzi powyżej lub zaimportuj plik CSV/XLSX.
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={menuState?.anchor || null}
        open={!!menuState}
        onClose={() => setMenuState(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuState && onAddBelow) onAddBelow(menuState.taskId);
            setMenuState(null);
          }}
          disabled={!onAddBelow}
        >
          Dodaj wiersz poniżej
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuState) onIndent(menuState.taskId);
            setMenuState(null);
          }}
        >
          Wcięcie →
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuState) onOutdent(menuState.taskId);
            setMenuState(null);
          }}
        >
          ← Cofnij wcięcie
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuState) onDelete(menuState.taskId);
            setMenuState(null);
          }}
          sx={{ color: '#B00020' }}
        >
          Usuń wiersz
        </MenuItem>
      </Menu>
    </Box>
  );
};

function cellInputStyle(col: ColumnDef): React.CSSProperties {
  return {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    color: 'inherit',
    padding: 0,
    textAlign: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left',
  };
}

export default ScheduleTaskTable;
